import React, { useState } from 'react';
import { LoadingSpinner } from './LoadingSpinner';

/**
 * Example component demonstrating LoadingSpinner usage
 * This file can be removed after integration is complete
 */
export const LoadingSpinnerExample: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const simulateLoading = () => {
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 3000);
  };

  const simulateSubmission = () => {
    setIsSubmitting(true);
    setTimeout(() => setIsSubmitting(false), 2000);
  };

  const simulateProcessing = () => {
    setIsProcessing(true);
    setTimeout(() => setIsProcessing(false), 4000);
  };

  return (
    <div className="p-8 space-y-8">
      <h1 className="text-2xl font-bold text-gray-900">LoadingSpinner Examples</h1>
      
      {/* Container Examples */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">Container Mode Examples</h2>
        
        {/* Large Spinner */}
        <div className="border rounded-lg p-4">
          <h3 className="text-md font-medium mb-2">Large Spinner (Page Loading)</h3>
          <button 
            onClick={simulateLoading}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 mb-4"
          >
            Simulate Page Loading
          </button>
          {isLoading ? (
            <LoadingSpinner 
              size="large" 
            />
          ) : (
            <div className="h-32 bg-gray-100 rounded flex items-center justify-center">
              <p className="text-gray-500">Content loaded</p>
            </div>
          )}
        </div>

        {/* Medium Spinner */}
        <div className="border rounded-lg p-4">
          <h3 className="text-md font-medium mb-2">Medium Spinner (Data Loading)</h3>
          <LoadingSpinner 
            size="medium" 
          />
        </div>

        {/* Different Color Schemes */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border rounded-lg p-4">
            <h3 className="text-md font-medium mb-2">Primary</h3>
            <LoadingSpinner />
          </div>
          <div className="border rounded-lg p-4">
            <h3 className="text-md font-medium mb-2">Secondary</h3>
            <LoadingSpinner />
          </div>
          <div className="border rounded-lg p-4">
            <h3 className="text-md font-medium mb-2">Accent</h3>
            <LoadingSpinner />
          </div>
        </div>
      </div>

      {/* Inline Examples */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">Inline Mode Examples</h2>
        
        {/* Button with Loading */}
        <div className="space-y-2">
          <h3 className="text-md font-medium">Button Loading State</h3>
          <button 
            onClick={simulateSubmission}
            disabled={isSubmitting}
            className={`px-6 py-2 rounded font-medium ${
              isSubmitting 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-green-500 hover:bg-green-600 text-white'
            }`}
          >
            {isSubmitting ? (
              <LoadingSpinner 
                size="small" 
              />
            ) : (
              'Save Changes'
            )}
          </button>
        </div>

        {/* Form Processing */}
        <div className="space-y-2">
          <h3 className="text-md font-medium">Form Processing</h3>
          <div className="flex items-center space-x-4">
            <button 
              onClick={simulateProcessing}
              disabled={isProcessing}
              className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600 disabled:bg-gray-400"
            >
              Process Data
            </button>
            {isProcessing && (
              <LoadingSpinner 
                size="small" 
              />
            )}
          </div>
        </div>

        {/* Small Inline Examples */}
        <div className="space-y-2">
          <h3 className="text-md font-medium">Small Inline Spinners</h3>
          <div className="flex space-x-4">
            <LoadingSpinner 
              size="small" 
            />
            <LoadingSpinner 
              size="small" 
            />
            <LoadingSpinner 
              size="small" 
            />
          </div>
        </div>
      </div>

      {/* Size Comparison */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">Size Comparison</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border rounded-lg p-4 text-center">
            <h3 className="text-md font-medium mb-2">Small (20px)</h3>
            <LoadingSpinner size="small" />
          </div>
          <div className="border rounded-lg p-4 text-center">
            <h3 className="text-md font-medium mb-2">Medium (40px)</h3>
            <LoadingSpinner size="medium" />
          </div>
          <div className="border rounded-lg p-4 text-center">
            <h3 className="text-md font-medium mb-2">Large (60px)</h3>
            <LoadingSpinner size="large" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoadingSpinnerExample;
